import React from 'react';
import { ChartStore } from './ChartStore';
import ChartDataProvider from './ChartDataProvider';
import GQEventEmitter, {
    EventSubscription,
    IArrowKey,
    E_ARROW_KEYS,
} from '../../services/EventsService';
import { Moment } from 'moment';
import { observer, inject } from 'mobx-react';
import rootStore from '../../RootStore';
import { VIEW } from '../../interfaces';
interface IChartContainerState {
    isMouseIn: boolean;
}

export interface Props {
    activeView: VIEW;
    chartStore?: ChartStore;
    height?: number;
    style?: React.CSSProperties;
    showSlider?: boolean;
    showToolbar?: boolean;
    showVirtualToday?: boolean;
    containerStyle?: React.CSSProperties;
    changeDataWhenDragStop?: boolean;
    isExportComponent?: boolean;
    showTooltip?: boolean;
}

@inject('chartStore')
@observer
export default class ChartContainer extends React.Component<
    Props,
    IChartContainerState
> {
    public static defaultProps: Partial<Props> = {
        showSlider: true,
        showToolbar: true,
        showTooltip: true,
        showVirtualToday: true,
        height: 157,
    };
    private keyboardEventSubscription: EventSubscription = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            isMouseIn: false,
        };
    }

    public componentDidMount() {
        this.keyboardEventSubscription = GQEventEmitter.registerOnArrowKey(
            this.onKeyboardKey
        );
    }

    public componentWillUnmount() {
        if (this.keyboardEventSubscription) {
            this.keyboardEventSubscription.remove();
        }
    }

    public render() {
        const { activeView, chartStore, height } = this.props;
        const overrideProps = {
            height,
            width: chartStore.width,
        };

        return (
            <div
                className="chart-container"
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseOut}>
                <div
                    style={{
                        flex: 1 /*todo: remove container div if not needed*/,
                    }}>
                    <ChartDataProvider
                        showSlider={this.props.showSlider}
                        activeView={activeView}
                        onVirualTodayChange={this.onVirualTodayChange}
                        onWheel={this.onWheel}
                        onPan={this.onPan}
                        hasToolbar={this.props.showToolbar}
                        showVirtualToday={this.props.showVirtualToday}
                        changeDataWhenDragStop={
                            this.props.changeDataWhenDragStop
                        }
                        isExportComponent={this.props.isExportComponent}
                        showTooltip={this.props.showTooltip}
                        {...overrideProps}
                    />
                </div>
            </div>
        );
    }

    private onMouseEnter = () => {
        this.setState({
            isMouseIn: true,
        });
    };

    private onMouseOut = () => {
        this.setState({
            isMouseIn: false,
        });
    };

    private onWheel = (
        isUp: boolean,
        rangeLeft: number = null,
        rangeRight: number = null
    ) => {
        const { chartStore } = this.props;
        const factor = isUp ? -10 : 10;
        const newRangeLeft =
            rangeLeft !== null ? rangeLeft : chartStore.rangeLeft + factor;
        const newRangeRight =
            rangeRight !== null ? rangeRight : chartStore.rangeRight + factor;
        chartStore.setNewRanges(newRangeLeft, newRangeRight);
        if (isUp) {
            const maxVirtualTodayPossibleLeft = chartStore.baseDate
                .clone()
                .startOf('day')
                .subtract(chartStore.rangeLeft, 'days');
            const maxVirtualTodayPossibleRight = chartStore.baseDate
                .clone()
                .startOf('day')
                .add(chartStore.rangeRight, 'days');
            if (
                this.props.chartStore.virtualToday.isBefore(
                    maxVirtualTodayPossibleLeft,
                    'd'
                )
            ) {
                this.onVirualTodayChange(maxVirtualTodayPossibleLeft);
            }

            if (
                this.props.chartStore.virtualToday.isAfter(
                    maxVirtualTodayPossibleRight,
                    'd'
                )
            ) {
                this.onVirualTodayChange(maxVirtualTodayPossibleRight);
            }
        }
    };

    private onPan = (range: number) => {
        const { chartStore } = this.props;
        const maybeNewVirtualToday = chartStore.setNewBaseDate(
            chartStore.baseDate.clone().add(range, 'days'),
            this.props.chartStore.virtualToday
        );
        if (maybeNewVirtualToday) {
            this.onVirualTodayChange(maybeNewVirtualToday);
        }
    };

    private onKeyboardKey = (key: IArrowKey) => {
        if (rootStore.insightsStore.annotationMode) {
            return;
        }
        if (this.state.isMouseIn || key.isShift) {
            switch (key.arrow) {
                case E_ARROW_KEYS.ARROW_DOWN: {
                    return this.onWheel(false);
                }
                case E_ARROW_KEYS.ARROW_UP: {
                    return this.onWheel(true);
                }
                case E_ARROW_KEYS.ARROW_RIGHT: {
                    return this.onPan(10);
                }
                case E_ARROW_KEYS.ARROW_LEFT: {
                    return this.onPan(-10);
                }
            }
        }
    };

    private onVirualTodayChange = (date: Moment) => {
        if (!date.isSame(this.props.chartStore.virtualToday, 'd')) {
            this.props.chartStore.setState({
                virtualToday: date.clone(),
            });
        }
    };

}
